// src/components/ContactPage.js

import React, { useState } from "react";
import { motion } from "framer-motion";

const Contact = () => {

  const [form, setForm] = useState({
    name: "",
    email: "",
    number: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [successToast, setSuccessToast] = useState("");
  const [errorToast, setErrorToast] = useState("");
  const [loading, setLoading] = useState(false);

  // Regex patterns
  const nameRegex = /^[A-Za-z\s]{3,50}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[6-9]\d{9}$/;
  const messageRegex = /^.{10,500}$/;

  // Validate single field (for live validation)
  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (!nameRegex.test(value))
          return "Only letters allowed (3–50 characters)";
        return "";

      case "email":
        if (!value.trim()) return "Email is required";
        if (!emailRegex.test(value))
          return "Enter valid email address";
        return "";

      case "number":
        if (!value.trim()) return "Phone is required";
        if (!phoneRegex.test(value))
          return "Enter valid 10-digit phone number";
        return "";

      case "message":
        if (!value.trim()) return "Message is required";
        if (!messageRegex.test(value))
          return "Message must be 10–500 characters";
        return "";

      default:
        return "";
    }
  };

  // Handle change with live validation
  const handleChange = (e) => {

    const { name, value } = e.target;

    let newValue = value;

    // Phone only digits
    if (name === "number") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setForm({
      ...form,
      [name]: newValue,
    });

    // live validation
    const error = validateField(name, newValue);

    setErrors({
      ...errors,
      [name]: error,
    });
  };

  // Validate full form
  const validateForm = () => {
    const newErrors = {};

    Object.keys(form).forEach((field) => {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    });

    return newErrors;
  };

  // Toast auto hide
  const showSuccessToast = (message) => {
    setSuccessToast(message);
    setTimeout(() => setSuccessToast(""), 3000);
  };

  const showErrorToast = (message) => {
    setErrorToast(message);
    setTimeout(() => setErrorToast(""), 3000);
  };

  // Submit
  const handleSubmit = async (e) => {

    e.preventDefault();

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showErrorToast("Please fix the errors");
      return;
    }

    try {

      setLoading(true);

      const response = await fetch(
        "https://arkam-site.onrender.com/api/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        }
      );

      if (response.ok) {

        showSuccessToast("Message sent successfully!");

        setForm({
          name: "",
          email: "",
          number: "",
          message: "",
        });

        setErrors({});
      } else {
        showErrorToast("Failed to send message");
      }

    } catch (error) {

      showErrorToast("Server error. Try again.");

    } finally {
      setLoading(false);
    }
  };

  // Dynamic border color
  const getBorder = (field) => {

    if (!form[field]) return "border-gray-300";

    return errors[field]
      ? "border-red-500"
      : "border-green-500";
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-gray-50 min-h-screen flex items-center justify-center px-4">

      {/* Toast Success */}
      {successToast && (
        <div className="fixed top-5 right-5 bg-green-500 text-white px-6 py-3 rounded shadow-lg z-50">
          {successToast}
        </div>
      )}

      {/* Toast Error */}
      {errorToast && (
        <div className="fixed top-5 right-5 bg-red-500 text-white px-6 py-3 rounded shadow-lg z-50">
          {errorToast}
        </div>
      )}

      <div className="bg-white shadow-xl rounded-xl w-full max-w-3xl p-8">

        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold text-center text-blue-600 mb-2">
            Contact Us
          </h1>

          <p className="text-center text-gray-500 mb-6">
            Send your message to our team
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Name */}
          <div>
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={form.name}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg outline-none transition ${getBorder("name")}`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <input
              type="text"
              name="email"
              placeholder="Email Address"
              value={form.email}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg outline-none transition ${getBorder("email")}`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <input
              type="text"
              name="number"
              placeholder="Phone Number"
              value={form.number}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg outline-none transition ${getBorder("number")}`}
            />
            {errors.number && (
              <p className="text-red-500 text-sm">{errors.number}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <textarea
              name="message"
              placeholder="Your Message"
              rows="5"
              value={form.message}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg outline-none transition ${getBorder("message")}`}
            />
            {errors.message && (
              <p className="text-red-500 text-sm">{errors.message}</p>
            )}
          </div>

          {/* Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.02 }}
            disabled={loading}
            className={`w-full py-3 rounded-lg font-semibold text-white flex justify-center items-center gap-2
              ${loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"}
            `}
          >

            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              "Send Message"
            )}

          </motion.button>

        </form>

      </div>
    </div>
  );
};

export default Contact;
